import { useMemo, useState } from 'react';
import { parseBulkImport } from '../features/players/bulkImport';
import { useConfig, type Player } from '../store/config';
import {
  Button,
  Card,
  Dialog,
  Field,
  StateBlock,
  TableShell,
  Td,
  TextInput,
  Th,
} from '../ui';
import { useToast } from '../ui/toast';

export default function PlayersPage() {
  const { config, setConfig, saveError } = useConfig();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Player>({ name: '', steamId: '' });
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);

  const players = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return config.players;
    return config.players.filter(
      (player) =>
        player.name.toLowerCase().includes(needle) || player.steamId.includes(needle),
    );
  }, [config.players, search]);

  const teamsOf = (steamId: string) =>
    config.teams.filter((team) => team.playerSteamIds.includes(steamId)).map((team) => team.name);

  const startAdd = () => {
    setEditingId('__new__');
    setDraft({ name: '', steamId: '' });
  };

  const startEdit = (player: Player) => {
    setEditingId(player.steamId);
    setDraft(player);
  };

  const save = async () => {
    const name = draft.name.trim();
    const steamId = draft.steamId.trim();
    if (!name || !steamId) {
      toast.error('Name and Steam ID are both required');
      return;
    }

    const isNew = editingId === '__new__';
    const clashes = config.players.some(
      (player) => player.steamId === steamId && player.steamId !== editingId,
    );
    if (clashes) {
      toast.error('That Steam ID is already saved');
      return;
    }

    const next = isNew
      ? [...config.players, { name, steamId }]
      : config.players.map((player) =>
          player.steamId === editingId ? { name, steamId } : player,
        );

    // Keep team membership pointing at the new ID when one is edited.
    const teams =
      !isNew && editingId && editingId !== steamId
        ? config.teams.map((team) => ({
            ...team,
            playerSteamIds: team.playerSteamIds.map((id) => (id === editingId ? steamId : id)),
          }))
        : config.teams;

    if (!await setConfig({ ...config, players: next, teams })) return;
    setEditingId(null);
  };

  const remove = (player: Player) => {
    setConfig({
      ...config,
      players: config.players.filter((candidate) => candidate.steamId !== player.steamId),
      teams: config.teams.map((team) => ({
        ...team,
        playerSteamIds: team.playerSteamIds.filter((id) => id !== player.steamId),
      })),
    });
  };

  const runImport = async () => {
    const result = parseBulkImport(importText, config.players, config.teams);
    if (!await setConfig({ ...config, players: result.players, teams: result.teams })) return;
    setImportOpen(false);
    setImportText('');
    toast.success(
      `Imported ${result.players.length - config.players.length} new player(s)`,
      result.skipped > 0 ? `${result.skipped} entr(ies) skipped` : undefined,
    );
  };

  return (
    <div className="space-y-6">
      {saveError && <p role="alert" className="text-sm text-warn">{saveError}</p>}
      <Card
        title="Players"
        description="Saved locally in this browser and used for invites."
        actions={
          <>
            <TextInput
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search"
              aria-label="Search players"
              className="h-8 w-44 text-xs"
            />
            <Button size="sm" onClick={() => setImportOpen(true)}>
              Bulk import
            </Button>
            <Button size="sm" variant="primary" onClick={startAdd}>
              Add player
            </Button>
          </>
        }
      >
        {config.players.length === 0 ? (
          <StateBlock
            title="No players yet"
            detail="Add them one at a time, or paste a roster with bulk import."
            action={
              <Button size="sm" variant="primary" onClick={startAdd}>
                Add player
              </Button>
            }
          />
        ) : (
          <TableShell>
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Steam ID</Th>
                <Th>Teams</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {players.map((player) => (
                <tr key={player.steamId}>
                  <Td className="font-medium text-ink">{player.name}</Td>
                  <Td className="font-mono text-xs text-ink-dim">{player.steamId}</Td>
                  <Td className="text-xs text-ink-dim">{teamsOf(player.steamId).join(', ') || '—'}</Td>
                  <Td>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" onClick={() => startEdit(player)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => remove(player)}>
                        Delete
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        )}

        {config.players.length > 0 && (
          <div className="mt-4 flex justify-end">
            <Button size="sm" variant="danger" onClick={() => setConfirmReset(true)}>
              Reset all players
            </Button>
          </div>
        )}
      </Card>

      <Dialog
        open={editingId !== null}
        onClose={() => setEditingId(null)}
        size="sm"
        title={editingId === '__new__' ? 'Add player' : 'Edit player'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditingId(null)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={save}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Name">
            {({ id }) => (
              <TextInput
                id={id}
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            )}
          </Field>
          <Field label="Steam ID" hint="The 64-bit ID, e.g. 76561198012345678">
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                value={draft.steamId}
                onChange={(event) => setDraft({ ...draft, steamId: event.target.value })}
              />
            )}
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Bulk import players"
        description="Paste JSON keyed by team, or one player per line."
        footer={
          <>
            <Button variant="ghost" onClick={() => setImportOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={runImport} disabled={!importText.trim()}>
              Import
            </Button>
          </>
        }
      >
        <textarea
          value={importText}
          onChange={(event) => setImportText(event.target.value)}
          rows={12}
          className="w-full rounded-md border border-line-strong bg-surface-2 px-3 py-2 font-mono text-xs text-ink focus:border-info focus:outline-none"
          placeholder={'Name,76561198012345678,Team A\nOther,76561198087654321,Team B'}
        />
        <p className="mt-2 text-xs text-ink-faint">
          Players already saved with the same Steam ID are updated, not duplicated.
        </p>
      </Dialog>

      <Dialog
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        size="sm"
        title="Remove every saved player?"
        description="Teams keep their names but lose their members."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmReset(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setConfig({
                  ...config,
                  players: [],
                  teams: config.teams.map((team) => ({ ...team, playerSteamIds: [] })),
                });
                setConfirmReset(false);
                toast.success('Players cleared');
              }}
            >
              Remove all
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-dim">This only affects this browser.</p>
      </Dialog>
    </div>
  );
}
